import React, { useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { themeColors } from '../theme';
import PlotExportBar from './PlotExportBar';
import { mppDot } from './MppMarker';
import ChartCrosshair from './ChartCrosshair';
import { jvXDomain, jvYDomain, niceStep, buildTicks } from '../utils/chartDomain';

// Shared light-JV chart panel — extracted from ResultsPanel so the
// simulation results tab and the Avarodha tab render the SAME chart
// (axis controls, light/dark curves, optional J0 what-if overlay curves)
// from the same results object without copy-pasting.

// Palette for Avarodha J0 what-if overlay curves. Shared with the
// overlay delta table / editor in AvarodhaPanel.
export const OVERLAY_COLORS = [
  '#f0a500', '#e85d75', '#a78bfa', '#22d3ee', '#39d353', '#fb7185',
];

// AxisInput: text input with spinner arrows for axis min/max/step
// controls. Syncs from parent only when NOT focused — this prevents the
// minus sign being eaten by the useEffect overwrite. Module-scope +
// exported so other chart panels (CVPanel) reuse the same control; takes
// the themeColors object as `c`.
export function AxisInput({ value, onChange, placeholder, step, c }) {
  const [local, setLocal] = React.useState(value == null ? '' : String(value));
  const focused = React.useRef(false);
  React.useEffect(() => {
    if (!focused.current) setLocal(value == null ? '' : String(value));
  }, [value]);
  const commit = (raw) => {
    const n = parseFloat(raw);
    if (!isNaN(n)) onChange(n);
    else if (raw === '' || raw === 'auto') onChange(null);
  };
  const stepVal = step || (value != null && Math.abs(value) < 2 ? 0.1 : 5);
  const nudge = (dir) => {
    const cur = parseFloat(local);
    const base = isNaN(cur) ? 0 : cur;
    const next = Math.round((base + dir * stepVal) * 1e6) / 1e6;
    setLocal(String(next));
    onChange(next);
  };
  const arrowStyle = {
    background: 'none', border: 'none', color: c.accent, cursor: 'pointer',
    padding: '0 2px', fontSize: 11, lineHeight: 1, fontFamily: 'monospace',
    userSelect: 'none',
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', background: c.input,
      border: `1px solid ${c.border}`, borderRadius: 4, overflow: 'hidden' }}>
      <input style={{
          background: c.input, color: c.inputText,
          fontFamily: 'IBM Plex Mono', fontSize: 13,
          padding: '3px 6px', textAlign: 'center',
          border: 'none', width: 54, borderRadius: 0,
        }}
        type="text" value={local} placeholder={placeholder || 'auto'}
        onChange={e => setLocal(e.target.value)}
        onFocus={() => { focused.current = true; }}
        onBlur={() => { focused.current = false; commit(local); }}
        onKeyDown={e => {
          if (e.key === 'Enter') e.target.blur();
          if (e.key === 'ArrowUp')   { e.preventDefault(); nudge(+1); }
          if (e.key === 'ArrowDown') { e.preventDefault(); nudge(-1); }
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'column',
                     borderLeft: `1px solid ${c.border}` }}>
        <button style={{ ...arrowStyle, borderBottom: `1px solid ${c.border}` }} onClick={() => nudge(+1)}>▲</button>
        <button style={arrowStyle} onClick={() => nudge(-1)}>▼</button>
      </div>
    </div>
  );
}

export const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#0d1117', border: '1px solid #2a3f55', borderRadius: 4,
      padding: '10px 14px', fontFamily: 'IBM Plex Mono, monospace', fontSize: 13,
    }}>
      <div style={{ color: '#8ba3bb', marginBottom: 6 }}>V = {Number(label).toFixed(3)} V</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.color, marginBottom: 2 }}>
          {p.name}: {Number(p.value).toFixed(3)}
        </div>
      ))}
    </div>
  );
};

// Voltage-dependent collection strip (v1.1.0). Shows the backend
// collection summary — eta_c at reverse / short circuit / MPP / Voc —
// and toggles the J_ph(V) overlay. Under superposition the strip states
// plainly that the photocurrent is constant by construction.
function pct(x) { return x == null || !isFinite(x) ? '—' : `${(100 * x).toFixed(1)}%`; }

function CollectionStrip({ c, collection, showPhoto, onToggle, photoColor }) {
  const cs = collection || {};
  const vd = !!cs.voltage_dependent;
  const cellStyle = { display: 'flex', flexDirection: 'column', minWidth: 78 };
  const labStyle = { fontSize: 10, color: c.textDim, letterSpacing: 0.5, textTransform: 'uppercase' };
  const valStyle = { fontSize: 13, color: c.text };
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        margin: '4px 0 6px', padding: '6px 10px', borderRadius: 4,
        border: `1px solid ${vd ? photoColor + '66' : c.border}`,
        fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, color: c.textMuted,
      }}
      title={cs.note || ''}
    >
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: c.text }}>
        <input type="checkbox" checked={!!showPhoto} onChange={onToggle} />
        <span style={{ color: photoColor }}>┄┄</span>
        J<sub>ph</sub>(V) = J<sub>light</sub> − J<sub>dark</sub>
      </label>
      {vd ? (
        <>
          <span style={{ color: c.textDim }}>collection η<sub>c</sub> = |J<sub>ph</sub>| / q∫G:</span>
          {cs.V_reverse != null && cs.eta_c_reverse != null && (
            <div style={cellStyle}>
              <span style={labStyle}>{Number(cs.V_reverse).toFixed(2)} V</span>
              <span style={valStyle}>{pct(cs.eta_c_reverse)}</span>
            </div>
          )}
          <div style={cellStyle}>
            <span style={labStyle}>short circuit</span>
            <span style={valStyle}>{pct(cs.eta_c_sc)}</span>
          </div>
          <div style={cellStyle}>
            <span style={labStyle}>MPP</span>
            <span style={valStyle}>{pct(cs.eta_c_mpp)}</span>
          </div>
          <div style={cellStyle}>
            <span style={labStyle}>V<sub>oc</sub></span>
            <span style={valStyle}>{pct(cs.eta_c_voc)}</span>
          </div>
          {cs.delta_eta_sc_to_mpp != null && (
            <div style={cellStyle}>
              <span style={labStyle}>SC → MPP loss</span>
              <span style={{ ...valStyle, color: cs.delta_eta_sc_to_mpp > 0.02 ? (c.amber || '#f0a500') : c.text }}>
                −{(100 * cs.delta_eta_sc_to_mpp).toFixed(1)} pt
              </span>
            </div>
          )}
          {cs.J_gen_mA_cm2 != null && (
            <div style={cellStyle}>
              <span style={labStyle}>q∫G</span>
              <span style={valStyle}>{Number(cs.J_gen_mA_cm2).toFixed(2)} mA/cm²</span>
            </div>
          )}
        </>
      ) : (
        <span style={{ color: c.amber || '#f0a500' }}>
          superposition: J<sub>ph</sub> constant by construction — no
          voltage-dependent collection modeled (use the Coupled JV mode)
        </span>
      )}
    </div>
  );
}

export default function JVChartPanel({
  jvLight = [], jvDark = [], metrics = {},
  overlayResults = null, theme = 'dark',
  chartId = 'jv-chart-container',
  // v1.1.0 — voltage-dependent collection: jvPhoto = [{V, J_ph, eta_c}]
  // (J_ph = J_light − J_dark), collection = the backend summary.
  jvPhoto = null, collection = null,
}) {
  const c = themeColors(theme);
  const axisStyle = { fill: c.plotText, fontSize: 14, fontFamily: 'IBM Plex Mono' };
  const tickStyle = { fill: c.plotText, fontSize: 13, fontFamily: 'IBM Plex Mono' };

  // null = auto (data-driven). Manual entry in the AxisInputs overrides.
  const [axisV, setAxisV] = useState({ min: null, max: null, step: null });
  const [axisJ, setAxisJ] = useState({ min: null, max: null, step: null });

  // Continuous crosshair readout — mouse x-pixel inside the chart div.
  const [hoverX, setHoverX] = useState(null);

  const hasDark = jvDark.length > 0;
  const hasPhoto = Array.isArray(jvPhoto) && jvPhoto.length > 1;
  // Photocurrent overlay defaults ON when it carries real information
  // (coupled solve); OFF for superposition where it is a flat line.
  const [showPhoto, setShowPhoto] = useState(true);
  const photoOn = hasPhoto && showPhoto;

  // Finite/sane points per series (|J| < 500 guards solver blow-ups).
  const sane = pts => (pts || []).filter(pt =>
    isFinite(pt.V) && isFinite(pt.J) && Math.abs(pt.J) < 500);
  const lightPts = sane(jvLight);
  const darkPts = sane(jvDark);
  const overlayPts = (overlayResults || []).map(ov => sane(ov.jv_curve));
  const allSeries = [lightPts, darkPts, ...overlayPts].filter(s => s.length > 0);

  // ── Data-driven axis domains (never hardcoded arrays) ──────────────
  // X spans the data extents and always extends past the JV zero
  // crossing so the Voc intercept stays visible (perovskite Voc 1.24 V
  // was clipped by the old fixed [0, 1.1] default). Y spans the J
  // extents inside the X window with ~5% padding; the forward-bias
  // blow-up is capped at |Jsc| by the helper. Manual overrides win;
  // the reported-Jsc rule is only a fallback when no points exist.
  const autoX = jvXDomain(allSeries);
  const xLo = axisV.min != null ? axisV.min : (autoX ? autoX[0] : 0);
  const xHi = axisV.max != null ? axisV.max : (autoX ? autoX[1] : 1.1);
  const autoY = jvYDomain(allSeries, xLo, xHi);
  const jscRep = Number(metrics?.Jsc);
  const jscFallback =
    Math.ceil(((Number.isFinite(jscRep) && jscRep !== 0 ? Math.abs(jscRep) : 30) + 5) / 5) * 5;
  const yLo = axisJ.min != null ? axisJ.min : (autoY ? autoY[0] : -jscFallback);
  const yHi = axisJ.max != null ? axisJ.max : (autoY ? autoY[1] : +jscFallback);

  const inWindow = pts => pts.filter(pt => pt.V >= xLo - 0.01 && pt.V <= xHi + 0.01);

  // Light JV — backend returns J < 0 for photocurrent (Q4 convention)
  const jvData = inWindow(lightPts).map(pt => ({ V: pt.V, 'J light': pt.J }));

  // Dark JV — the backend returns the standard convention directly:
  // dark forward current POSITIVE. Plot as-is.
  const jvDarkData = inWindow(darkPts).map(pt => ({ V: pt.V, 'J dark': pt.J }));

  // Photocurrent J_ph(V) = J_light − J_dark (Q4: negative). Under the
  // coupled solve this bends toward zero with forward bias — that IS
  // the voltage-dependent collection; under superposition it is flat.
  const photoPts = hasPhoto
    ? jvPhoto.filter(p => isFinite(p.V) && isFinite(p.J_ph) && Math.abs(p.J_ph) < 500)
    : [];
  const jvPhotoData = photoOn
    ? inWindow(photoPts).map(pt => ({ V: pt.V, 'J photo': pt.J_ph }))
    : [];
  const photoColor = c.amber || '#f0a500';

  // Ticks — manual step wins; otherwise a nice 1/2/5 step over the range.
  const vStepEff = axisV.step || niceStep(xHi - xLo, 7);
  const vTicks = buildTicks(xLo, xHi, vStepEff);
  const jStepEff = axisJ.step || niceStep(yHi - yLo, 7);
  const jTicks = buildTicks(yLo, yHi, jStepEff);

  // Series handed to ChartCrosshair — same plotted points, only the
  // hover readout interpolates between them.
  const hoverSeries = [
    { name: 'J light', color: c.accent,
      points: jvData.map(p => ({ x: p.V, y: p['J light'] })) },
    ...(hasDark ? [{ name: 'J dark', color: c.green,
      points: jvDarkData.map(p => ({ x: p.V, y: p['J dark'] })) }] : []),
    ...(photoOn ? [{ name: 'J photo', color: photoColor,
      points: jvPhotoData.map(p => ({ x: p.V, y: p['J photo'] })) }] : []),
    ...overlayPts.map((pts, i) => ({
      name: (overlayResults && overlayResults[i] && overlayResults[i].label) || `overlay ${i + 1}`,
      color: OVERLAY_COLORS[i % OVERLAY_COLORS.length],
      points: inWindow(pts).map(p => ({ x: p.V, y: p.J })),
    })),
  ].filter(s => s.points.length > 1);

  return (
    <div className="chart-panel">
      <div className="chart-title">J–V Characteristic</div>

      {/* Axis controls */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8,
        fontFamily: 'IBM Plex Mono', fontSize: 13, color: c.textMuted, flexWrap: 'wrap' }}>
        <span>V:</span>
        <AxisInput c={c} value={axisV.min}  onChange={v => setAxisV(a => ({ ...a, min: v }))}  placeholder="auto" step={axisV.step ?? 0.1} />
        <span>→</span>
        <AxisInput c={c} value={axisV.max}  onChange={v => setAxisV(a => ({ ...a, max: v }))}  placeholder="auto" step={axisV.step ?? 0.1} />
        <AxisInput c={c} value={axisV.step} onChange={v => setAxisV(a => ({ ...a, step: v }))} placeholder="step" step={0.05} />
        <span style={{ marginLeft: 6 }}>J:</span>
        <AxisInput c={c} value={axisJ.min}  onChange={v => setAxisJ(a => ({ ...a, min: v }))}  placeholder="auto" step={axisJ.step ?? 5} />
        <span>→</span>
        <AxisInput c={c} value={axisJ.max}  onChange={v => setAxisJ(a => ({ ...a, max: v }))}  placeholder="auto" step={axisJ.step ?? 5} />
        <AxisInput c={c} value={axisJ.step} onChange={v => setAxisJ(a => ({ ...a, step: v }))} placeholder="step" step={1} />
        <button onClick={() => { setAxisV({ min: null, max: null, step: null }); setAxisJ({ min: null, max: null, step: null }); }}
          style={{
            background: c.btnSecondary, color: c.btnSecondaryText,
            border: `1px solid ${c.border}`, borderRadius: 4,
            padding: '3px 10px', fontSize: 13,
            fontFamily: 'IBM Plex Mono, monospace',
            cursor: 'pointer',
          }}>
          Reset
        </button>
      </div>

      <div id={chartId} style={{ width: '100%', height: 250 }}
        onMouseMove={e => {
          const r = e.currentTarget.getBoundingClientRect();
          setHoverX(e.clientX - r.left);
        }}
        onMouseLeave={() => setHoverX(null)}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart margin={{ top: 8, right: 20, bottom: 28, left: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={c.plotGrid} />
          <XAxis dataKey="V" type="number" domain={[xLo, xHi]} ticks={vTicks}
            tickFormatter={v => v.toFixed(2)} tick={tickStyle}
            label={{ value: 'Voltage (V)', position: 'insideBottom', offset: -14, ...axisStyle }} />
          <YAxis domain={[yLo, yHi]} ticks={jTicks} allowDataOverflow={true}
            tickFormatter={v => v.toFixed(1)} tick={tickStyle}
            label={{ value: 'J (mA/cm²)', angle: -90, position: 'insideLeft', offset: -4, textAnchor: 'middle', ...axisStyle }} />
          <ReferenceLine y={0} stroke={c.plotGrid} strokeDasharray="4 2" />
          <ReferenceLine x={0} stroke={c.plotGrid} strokeDasharray="4 2" />
          <Line data={jvData} type="monotone" dataKey="J light" stroke={c.accent} strokeWidth={2.5} dot={false} isAnimationActive={false} />
          {/* MPP marker at (Vmp, Jmp), same color as the light curve (MppMarker.js) */}
          {mppDot({ metrics, jvCurve: jvLight, color: c.accent })}
          {hasDark && <Line data={jvDarkData} type="monotone" dataKey="J dark" stroke={c.green} strokeWidth={1.5} dot={false} strokeDasharray="5 3" isAnimationActive={false} />}
          {photoOn && <Line data={jvPhotoData} type="monotone" dataKey="J photo" stroke={photoColor} strokeWidth={1.5} dot={false} strokeDasharray="2 3" isAnimationActive={false} />}
          {/* Avarodha J0 what-if overlay curves — dashed, one color per
              overlay; name = overlay label (shown in the crosshair readout + legend). */}
          {overlayPts.map((pts, i) => (
            <Line key={`ov-${i}`} type="monotone" dataKey="J"
              name={(overlayResults && overlayResults[i] && overlayResults[i].label) || `overlay ${i + 1}`}
              data={inWindow(pts).map(pt => ({ V: pt.V, J: pt.J }))}
              stroke={OVERLAY_COLORS[i % OVERLAY_COLORS.length]}
              strokeWidth={1.5} strokeDasharray="6 4" dot={false} isAnimationActive={false} />
          ))}
          {/* Continuous crosshair — interpolated (V, J) readout, replaces
              the nearest-point-snapping Tooltip. */}
          <ChartCrosshair hoverX={hoverX} series={hoverSeries}
            xLabel="V" xUnit="V" yUnit="mA/cm²" />
        </LineChart>
      </ResponsiveContainer>
      </div>
      {hasPhoto && (
        <CollectionStrip
          c={c} collection={collection} showPhoto={showPhoto}
          onToggle={() => setShowPhoto(x => !x)} photoColor={photoColor}
        />
      )}
      {overlayResults && overlayResults.length > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 12, margin: '2px 0 4px',
          fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: c.textMuted,
        }}>
          <span><span style={{ color: c.accent }}>——</span> simulated</span>
          {overlayResults.map((ov, i) => (
            <span key={`ovleg-${i}`}>
              <span style={{ color: OVERLAY_COLORS[i % OVERLAY_COLORS.length] }}>╌╌</span>
              {' '}{ov.label}
            </span>
          ))}
        </div>
      )}
      <PlotExportBar
        chartId={chartId}
        csvData={(() => {
          const dark = new Map((jvDark || []).map(p => [p.V, p.J]));
          const photo = new Map((jvPhoto || []).map(p => [p.V, p]));
          return jvLight.map(p => ({
            V: p.V,
            J_light_mA_cm2: -p.J,
            J_dark_mA_cm2:  dark.has(p.V) ? -dark.get(p.V) : '',
            ...(hasPhoto ? {
              J_photo_mA_cm2: photo.has(p.V) ? -photo.get(p.V).J_ph : '',
              collection_eta_c: photo.has(p.V) && photo.get(p.V).eta_c != null
                ? photo.get(p.V).eta_c : '',
            } : {}),
          }));
        })()}
        csvFilename="jvista_jv"
        plotFilename="jv"
        c={c}
        disabled={jvLight.length === 0}
      />
    </div>
  );
}
